/**
 * Real API client (SPEC §9, under /api/v1). Session is an HTTP-only cookie; state-changing
 * requests echo the readable csrf_token cookie in X-CSRF-Token.
 *
 * Endpoints land with the backend slices (docs/PROGRESS.md); until then the UI runs on the
 * demo adapter (VITE_DEMO=1).
 */
import {
  ApiError,
  type Category,
  type Entry,
  type ExpenseApi,
  type Favourite,
  type Handover,
  type HandoverPerson,
  type ItemPurchase,
  type Me,
  type PendingCashIn,
  type SaveResult,
  type SpenderNotificationList,
  type Wallet,
} from './types.ts';

const BASE = '/api/v1';

function csrfToken(): string {
  const m = /(?:^|;\s*)csrf_token=([^;]+)/.exec(document.cookie);
  return m?.[1] ? decodeURIComponent(m[1]) : '';
}

export async function request<T>(method: string, path: string, body?: unknown, entityId?: string): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-CSRF-Token'] = csrfToken();
  if (entityId) headers['X-Entity-Id'] = entityId;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(BASE + path, { method, headers, body: payload, credentials: 'same-origin' });
  } catch {
    // No answer at all: offline, Wi-Fi dropped, or the server is down.
    throw new ApiError('network', 'Could not reach the server', undefined, 0);
  }
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data ?? {}) as { code?: string; message?: string; field?: string };
    throw new ApiError(err.code ?? `http_${res.status}`, err.message ?? statusMessage(res), err.field, res.status);
  }
  return data as T;
}

/**
 * GET a file (e.g. an Excel export) and save it in the browser's Downloads.
 * The file name comes from the server's Content-Disposition, else `fallbackName`.
 */
/** A plain message when the server sent none: a proxy's "Bad Gateway" means the app server
 *  is restarting or down for a moment. */
function statusMessage(res: Response): string {
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    return 'The server is not answering right now. Please try again in a moment.';
  }
  return res.statusText || 'Something went wrong';
}

/** A file from the server, without saving it (e.g. a PDF to print). */
export async function fetchFile(path: string, entityId?: string): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      headers: entityId ? { 'X-Entity-Id': entityId } : {},
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('network', 'Could not reach the server', undefined, 0);
  }
  if (!res.ok) {
    const err = ((await res.json().catch(() => null)) ?? {}) as { code?: string; message?: string };
    throw new ApiError(err.code ?? `http_${res.status}`, err.message ?? statusMessage(res), undefined, res.status);
  }
  return res.blob();
}

export async function download(path: string, fallbackName: string, entityId?: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      headers: entityId ? { 'X-Entity-Id': entityId } : {},
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('network', 'Could not reach the server', undefined, 0);
  }
  if (!res.ok) {
    const err = ((await res.json().catch(() => null)) ?? {}) as { code?: string; message?: string };
    throw new ApiError(err.code ?? `http_${res.status}`, err.message ?? statusMessage(res), undefined, res.status);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before freeing the file.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Entities from the last /me call; most endpoints are entity-scoped. */
let entityIds: string[] = [];

async function perEntity<T>(fn: (entityId: string) => Promise<T[]>): Promise<T[]> {
  return (await Promise.all(entityIds.map(fn))).flat();
}

export const httpApi: ExpenseApi = {
  async requestOtp(email) {
    await request('POST', '/auth/request-otp', { email });
  },
  async verifyOtp(email, code) {
    const me = await request<Me>('POST', '/auth/verify-otp', { email, code });
    entityIds = me.entities.map((e) => e.id);
    return me;
  },
  async logout() {
    await request('POST', '/auth/logout');
  },
  async me() {
    try {
      const me = await request<Me>('GET', '/me');
      entityIds = me.entities.map((e) => e.id);
      return me;
    } catch (e) {
      // Not signed in (or the session expired / was revoked): show the login screen.
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  async setLanguage(language) {
    await request('PATCH', '/me', { language });
  },
  wallets: () => perEntity((id) => request<Wallet[]>('GET', `/entities/${id}/wallets`, undefined, id)),
  pendingCashIns: () => perEntity((id) => request<PendingCashIn[]>('GET', `/entities/${id}/cash-in/unacknowledged`, undefined, id)),
  async acknowledge(cashInId, entityId) {
    await request('POST', `/transactions/${cashInId}/acknowledge`, undefined, entityId);
  },
  handoverPeople: (id) => request<HandoverPerson[]>('GET', `/entities/${id}/handover/people`, undefined, id),
  myHandovers: (id) => request<Handover[]>('GET', `/entities/${id}/handovers`, undefined, id),
  async undo(txnId, entityId) {
    await request('POST', `/transactions/${txnId}/undo`, undefined, entityId);
  },
  handOver: (h) =>
    request<{ handover: Handover; spendable: number }>(
      'POST',
      `/entities/${h.entityId}/handovers`,
      { to_wallet_id: h.toWalletId, amount: h.amount, note: h.note.trim() || null },
      h.entityId,
    ),
  categories: (id) => request<Category[]>('GET', `/entities/${id}/categories`, undefined, id),
  favourites: (id) => request<Favourite[]>('GET', `/entities/${id}/favourites`, undefined, id),
  recentItems: (id, categoryId) => request<ItemPurchase[]>('GET', `/entities/${id}/items/recent?category_id=${categoryId}`, undefined, id),
  lastBought: (id, categoryId, itemName) =>
    request<ItemPurchase | null>(
      'GET',
      `/entities/${id}/items/last?category_id=${categoryId}&name=${encodeURIComponent(itemName)}`,
      undefined,
      id,
    ),
  myEntries: () => perEntity((id) => request<Entry[]>('GET', `/entities/${id}/transactions?mine=true`, undefined, id)),
  async saveExpense(e) {
    const form = new FormData();
    const fields: Record<string, string | null> = {
      client_ref: e.clientRef,
      category_id: e.categoryId,
      item_name: e.itemName,
      quantity: e.quantity === null ? null : String(e.quantity),
      unit: e.unit,
      amount: String(e.amount),
      txn_date: e.txnDate,
      no_receipt_reason: e.noReceiptReason,
      lat: e.location ? String(e.location.lat) : null,
      lng: e.location ? String(e.location.lng) : null,
      accuracy_m: e.location ? String(e.location.accuracyM) : null,
      located_at: e.location?.locatedAt ?? null,
    };
    for (const [k, v] of Object.entries(fields)) if (v !== null) form.append(k, v);
    e.photos.forEach((p, i) => form.append('receipts', p, `receipt-${i + 1}.jpg`));
    return e.correctsId
      ? request<SaveResult>('PATCH', `/transactions/${e.correctsId}`, form, e.entityId)
      : request<SaveResult>('POST', `/entities/${e.entityId}/expenses`, form, e.entityId);
  },
  async deleteEntry(entryId, entityId) {
    await request('DELETE', `/transactions/${entryId}`, undefined, entityId);
  },
  notifications: () => request<SpenderNotificationList>('GET', '/me/notifications'),
  async markNotificationRead(id) {
    await request('POST', `/me/notifications/${id}/read`);
  },
  async markAllNotificationsRead() {
    await request('POST', '/me/notifications/read-all');
  },
  replyToFix: (entryId, entityId, message) => request<Entry>('POST', `/transactions/${entryId}/reply`, { message }, entityId),
  async receiptUrl(receiptId, entityId) {
    return (await request<{ url: string }>('GET', `/receipts/${receiptId}/url`, undefined, entityId)).url;
  },
};
